import * as React from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import moment from 'moment-timezone';
import { Table, Input, Button, Dropdown, Space, Modal, message, Tag } from 'antd';
import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useFunnelAnalyses } from '@src/services/queries';
import { useDeleteFunnelAnalysis } from '@src/services/mutations';
import { pageNavsStore } from '@shared/store';
import * as Styles from './styled';

const FunnelAnalyses = () => {
  const navigate = useNavigate();
  const [modal, contextHolder] = Modal.useModal();
  const { data, isFetching, refetch } = useFunnelAnalyses();
  const { mutateAsync: deleteFunnelAnalysis } = useDeleteFunnelAnalysis();
  const [search, setSearch] = React.useState<string>();
  const [loadingIds, setLoadingIds] = React.useState<string[]>([]);
  const loadingIdSet = React.useMemo(() => new Set(loadingIds), [loadingIds]);

  type DataItem = NonNullable<typeof data>[0];

  const columns = React.useMemo<GetProp<typeof Table<DataItem>, 'columns'>>(
    () => [
      {
        title: 'Analysis Name',
        dataIndex: ['config', 'name'],
        render: (value, record) => <NavLink to={`/funnels/saved/${record.id}`}>{value}</NavLink>,
        sorter: (a, b) => {
          return (a.config.name ?? '').localeCompare(b.config.name ?? '');
        },
      },
      {
        title: 'Milestone Event',
        dataIndex: ['config', 'critical_events_detail'],
        render: (value) => {
          return value[value.length - 1]?.name ?? <Tag color="red">Event Deleted</Tag>;
        },
        sorter: (a, b) => {
          const aEventId = a.config.critical_events[a.config.critical_events.length - 1];
          const bEventId = b.config.critical_events[b.config.critical_events.length - 1];
          const aEventName =
            a.config.critical_events_detail[a.config.critical_events_detail.length - 1]?.name ??
            aEventId;
          const bEventName =
            b.config.critical_events_detail[b.config.critical_events_detail.length - 1]?.name ??
            bEventId;

          return aEventName.localeCompare(bEventName);
        },
      },
      {
        title: 'Updated By',
        dataIndex: 'updated_by',
        sorter: (a, b) => {
          return (a.updated_by ?? '').localeCompare(b.updated_by ?? '');
        },
      },
      {
        title: 'Last Modified',
        dataIndex: 'updated_at',
        render: (value: string) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
        sorter: (a, b) => {
          return moment(a.updated_at).unix() - moment(b.updated_at).unix();
        },
        defaultSortOrder: 'descend',
      },
      {
        title: 'Actions',
        dataIndex: 'id',
        render: (value: string) => {
          return (
            <Dropdown
              disabled={loadingIdSet.has(value)}
              menu={{
                items: [
                  { key: 'drilldown', label: 'Go to Analysis' },
                  { key: 'delete', label: 'Delete' },
                ],
                onClick: ({ key }) => {
                  if (key === 'drilldown') {
                    void navigate(`/funnels/saved/${value}`);
                  }

                  if (key === 'delete') {
                    const confirmInstance = modal.confirm({
                      title: 'Are you sure you want to delete this Funnel Analysis?',
                      okButtonProps: { danger: true, type: 'primary' },
                      okText: 'Delete',
                      onOk: async () => {
                        setLoadingIds((prev) => [...prev, value]);
                        confirmInstance.update({ cancelButtonProps: { disabled: true } });

                        await deleteFunnelAnalysis(value)
                          .then(() => {
                            message.success('Funnel Analysis deleted successfully');
                            pageNavsStore.getState().remove(value);
                          })
                          .catch(() => {
                            message.error('Failed to delete Funnel Analysis');
                          })
                          .finally(() => {
                            setLoadingIds((prev) => prev.filter((id) => id !== value));
                          })
                          .then(() => refetch());
                      },
                    });
                  }
                },
              }}
            >
              <Button loading={loadingIdSet.has(value)} icon={<MoreOutlined />} type="text" />
            </Dropdown>
          );
        },
      },
    ],
    [loadingIdSet, navigate, modal, deleteFunnelAnalysis, refetch],
  );

  const tableData = React.useMemo(() => {
    if (!data || !search) {
      return data;
    }

    return data.filter(
      (item) =>
        item.config.name?.toLowerCase().includes(search.toLowerCase()) ||
        item.updated_by?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [data, search]);

  return (
    <>
      <Space direction="vertical" size={16} style={{ display: 'flex' }}>
        <Space size={16} align="center" style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Styles.TableCount>Total {data?.length ?? 0} Funnel Analyses</Styles.TableCount>
          <Button
            variant="outlined"
            color="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              void navigate(`/funnels/temp`, {
                state: {},
              });
            }}
          >
            Funnel Analysis
          </Button>
          <Input.Search allowClear placeholder="Search..." onSearch={setSearch} />
        </Space>
        <Table
          rowKey="id"
          dataSource={tableData}
          columns={columns}
          loading={isFetching}
          scroll={{ x: true }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          sortDirections={['ascend', 'descend', 'ascend']}
        />
      </Space>
      {contextHolder}
    </>
  );
};

export default React.memo(FunnelAnalyses);
